import { ApiError, env, fetchWithAuth } from '@botto/core';

function joinUrl(path: string): string {
  const base = env.smsApiUrl.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

function buildHref(path: string, query?: Record<string, QueryValue>): string {
  const joined = joinUrl(path);
  const url = new URL(joined, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  applyQuery(url, query);
  return joined.startsWith('http') ? url.href : `${url.pathname}${url.search}`;
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown = null;
    try {
      const ct = res.headers.get('content-type') || '';
      body = ct.includes('application/json') ? await res.json() : await res.text();
    } catch {
      /* ignore */
    }
    throw new ApiError(`HTTP ${res.status} ${res.statusText}`, res.status, body);
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  const text = await res.text();
  if (!text) return null as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as T;
  }
}

type QueryValue = string | number | boolean | undefined | null | Array<string | number | boolean>;

function applyQuery(url: URL, query?: Record<string, QueryValue>): void {
  if (!query) return;
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item !== undefined && item !== null && item !== '') {
          url.searchParams.append(k, String(item));
        }
      }
    } else {
      url.searchParams.set(k, String(v));
    }
  }
}

async function smsFetch(path: string, init: RequestInit, query?: Record<string, QueryValue>): Promise<Response> {
  return fetchWithAuth(buildHref(path, query), init, 'sms');
}

/** GET with JSON body (operator stats — non-standard OpenAPI). */
export async function smsGetWithBody<T>(path: string, body: unknown): Promise<T> {
  const res = await smsFetch(path, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(res);
}

export async function smsGet<T>(path: string, query?: Record<string, QueryValue>): Promise<T> {
  const res = await smsFetch(path, { method: 'GET' }, query);
  return parseResponse<T>(res);
}

export async function smsPost<T>(path: string, body: unknown): Promise<T> {
  const res = await smsFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(res);
}

/** Download binary response (Excel export, etc.). */
export async function smsGetBlob(
  path: string,
  query?: Record<string, QueryValue>,
): Promise<{ blob: Blob; filename: string | null }> {
  const res = await smsFetch(path, { method: 'GET' }, query);
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.clone().text();
    } catch {
      /* ignore */
    }
    throw new ApiError(`HTTP ${res.status} ${res.statusText}`, res.status, body);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  const filename = match ? decodeURIComponent(match[1].replace(/"/g, '')) : null;
  return { blob, filename };
}

export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
